import * as Yup from "yup";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import MuiButton from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import React, { FC, useState } from "react";
import { ErrorMessage, Field, Form, Formik, FormikHelpers } from "formik";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";

import api from "../../../../api";
import { useGlobalSelectedAdvertiser } from "../../../../hooks/useGlobalSelectedAdvertiser";

type Props = {
  isOpen: boolean;
  shouldSetDefaultAdvertiser: boolean;
  handleClose: () => void;
};

type FormValues = {
  name: string;
  description: string;
  advertiserId: number | null;
};

const validationSchema = Yup.object({
  name: Yup.string().required("Required field."),
});

const formInitialValues: FormValues = {
  name: "",
  description: "",
  advertiserId: null,
};

const ModalHeader = styled("div")(({ theme }: { theme: Theme }) => ({
  padding: "20px 24px",
  borderBottom: `1px solid ${theme.palette.brand.gray100}`,
  background: theme.palette.brand.white,
}));

const ModalTitle = styled(Typography)(({ theme }: { theme: Theme }) => ({
  fontSize: 16,
  fontWeight: 600,
  color: theme.palette.brand.gray900,
}));

const StyledDialogContent = styled(DialogContent)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.gray50,
  padding: "24px !important",
  display: "flex",
  flexDirection: "column",
  gap: 4,
  minWidth: 480,
}));

const FieldWrapper = styled("div")({
  display: "flex",
  flexDirection: "column",
  marginBottom: 12,
});

const StyledTextField = styled(TextField)(({ theme }: { theme: Theme }) => ({
  "& .MuiOutlinedInput-root": {
    background: theme.palette.brand.white,
    borderRadius: 6,
    "& fieldset": {
      borderColor: theme.palette.brand.gray200,
    },
    "&:hover fieldset": {
      borderColor: theme.palette.brand.gray300,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.brand.blueAccent,
    },
  },
  "& .MuiInputBase-root": {
    fontSize: 14,
  },
}));

const ErrorText = styled("span")(({ theme }: { theme: Theme }) => ({
  fontSize: 12,
  color: theme.palette.brand.errorRed,
  minHeight: 16,
  marginTop: 4,
  display: "block",
}));

const StyledDialogActions = styled(DialogActions)(({ theme }: { theme: Theme }) => ({
  padding: "16px 24px",
  borderTop: `1px solid ${theme.palette.brand.gray100}`,
  background: theme.palette.brand.white,
}));

const CancelButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.gray500,
  textTransform: "none",
  fontWeight: 500,
  borderColor: theme.palette.brand.gray200,
  "&:hover": {
    background: theme.palette.brand.gray50,
    borderColor: theme.palette.brand.gray300,
  },
}));

const SubmitButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.coral,
  color: theme.palette.brand.white,
  textTransform: "none",
  fontWeight: 500,
  borderRadius: 6,
  padding: "6px 20px",
  minWidth: 100,
  boxShadow: "none",
  "&:hover": {
    background: theme.palette.brand.coralHover,
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    background: theme.palette.brand.gray200,
    color: theme.palette.brand.gray500,
  },
}));

const CreateSegmentModalRedesign: FC<Props> = ({ isOpen, handleClose }) => {
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { adLibAdvertiserId } = useGlobalSelectedAdvertiser();

  const handleSubmit = async (values: FormValues, actions: FormikHelpers<FormValues>) => {
    const { name } = values;
    if (!adLibAdvertiserId) {
      setErrorMessage("Please select an advertiser from the profile menu.");
      return;
    }

    try {
      setIsLoading(true);
      await api.createSegment(name, adLibAdvertiserId);
      setIsLoading(false);
      handleClose();
    } catch (error) {
      const err = error as any;
      setIsLoading(false);
      actions.resetForm();
      setErrorMessage(`There was an error: ${err.message}`);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={isLoading ? undefined : handleClose}
      aria-labelledby="create-segment-modal-title"
      maxWidth="sm"
    >
      <Formik
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnBlur={false}
      >
        {({ values }) => (
          <>
            <ModalHeader>
              <ModalTitle id="create-segment-modal-title">Create New Re-targeting Segment</ModalTitle>
            </ModalHeader>

            <Form>
              <StyledDialogContent>
                <FieldWrapper>
                  <Field
                    as={StyledTextField}
                    name="name"
                    label="Name *"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={values.name}
                    error={!!errorMessage}
                  />
                  <ErrorText>
                    <ErrorMessage name="name" />
                  </ErrorText>
                </FieldWrapper>

                <FieldWrapper>
                  <Field
                    as={StyledTextField}
                    name="description"
                    label="Description"
                    variant="outlined"
                    size="small"
                    fullWidth
                    multiline
                    rows={3}
                    value={values.description}
                  />
                  <ErrorText />
                </FieldWrapper>

                {errorMessage && <ErrorText style={{ marginTop: 8 }}>{errorMessage}</ErrorText>}
              </StyledDialogContent>

              <StyledDialogActions>
                <CancelButton variant="outlined" onClick={isLoading ? undefined : handleClose} disabled={isLoading}>
                  Cancel
                </CancelButton>
                <SubmitButton type="submit" variant="contained" disabled={isLoading || !adLibAdvertiserId}>
                  {isLoading ? <CircularProgress size={20} style={{ color: "inherit" }} /> : "Submit"}
                </SubmitButton>
              </StyledDialogActions>
            </Form>
          </>
        )}
      </Formik>
    </Dialog>
  );
};

export default CreateSegmentModalRedesign;
